import React, { useEffect, useRef, useState } from 'react';
import './styles/EndModal.css';
import { getBestStreak, getGameDataProperty, getPlayedBoardNumbers, getPlayStreak } from '../../services/state';
import { convertMillisToSeconds, formatOperatorSymbol, getBoardNavigation, shareOrCopyText } from '../../helper/helperFunctions';
import BoardButton from '../BoardButton';

// Map action to emoji for sharing
const mapActionToEmoji = (act) => {
    if (act === 'U') return '🟨';
    if (act === 'C') return '⬛';
    if (typeof act === 'string' && /^[+\-*/]/.test(act)) return '🟩';
    return '⬜';
};

// Chunk emojis into lines of 5
const chunkEmojis = (emojiStr) => Array.from(emojiStr).reduce((acc, char, idx) =>
    idx % 5 === 0 ? [...acc, char] : [...acc.slice(0, -1), acc[acc.length - 1] + char], []
).join('\n');

// Exported helper so tests can call it directly
export const buildShareText = (board, isArchived = false, streak = 0) => {
    const score = getGameDataProperty(board, 'gameScore') || 0;
    const actionHistory = getGameDataProperty(board, 'action_history') || [];
    const emojiRow = actionHistory.map(mapActionToEmoji).join('') || '⬜';
    const body = chunkEmojis(emojiRow);
    const boardUrl = isArchived ? `www.thecombos.app/archive/${board}` : 'www.thecombos.app';
    const streakLine = streak > 0 ? `\n🔥 ${streak} day${streak === 1 ? '' : 's'} streak` : '';
    return `The Combos #${board}\nScore: ${score}${streakLine}\n\n${body}\n\n${boardUrl}`;
};

export const getScoreClass = (score) => {
    const numericScore = Number(score);
    if (numericScore <= 10) return 'end-modal-score-dark-red';
    if (numericScore >= 11 && numericScore <= 30) return 'end-modal-score-red';
    if (numericScore >= 31 && numericScore <= 70) return 'end-modal-score-yellow';
    if (numericScore >= 71 && numericScore <= 90) return 'end-modal-score-green';
    if (numericScore >= 91) return 'end-modal-score-special';
    return '';
};

// New: map action to display text and color for modal
const mapActionToLabelAndColor = (act) => {
    if (act === 'U') return { text: 'undo', color: '#f1c40f' }; // yellow
    if (act === 'C') return { text: 'clear', color: '#929d9e' }; // gray
    if (typeof act === 'string' && /^[+\-*/]\d+/.test(act)) {
        return { text: formatOperatorSymbol(act), color: '#00d93a' }; // green for operators like +7 or -3
    }
    // fallback
    return { text: String(act), color: '#000' };
};

// Split the flat action history into one group per attempt. A new attempt starts
// whenever the board is back at the starting number: either from an explicit 'C'
// (clear), or from undoing every move made since the last reset — both leave the
// player at the same starting point, so both count as a fresh attempt boundary.
// The last group is the one that actually reached the target.
const groupActionsByAttempt = (history) => {
    const groups = [[]];
    let depth = 0;
    history.forEach((action) => {
        groups[groups.length - 1].push(action);
        if (action === 'C') {
            depth = 0;
            groups.push([]);
            return;
        }
        depth = action === 'U' ? Math.max(0, depth - 1) : depth + 1;
        if (action === 'U' && depth === 0) groups.push([]);
    });
    return groups.filter((group) => group.length > 0);
};

// Produce JSX of the action history as chips, grouped by attempt, with the final
// (successful) attempt visually called out.
const getActionHistoryElements = (boardNum, historyRef) => {
    const history = getGameDataProperty(boardNum, 'action_history') || [];
    if (!history.length) return null;
    const groups = groupActionsByAttempt(history);

    return (
        <div ref={historyRef} className='end-modal-history' style={{ fontFamily: 'var(--font2)' }}>
            {groups.map((group, groupIdx) => {
                const isFinal = groupIdx === groups.length - 1;
                return (
                    <div key={groupIdx} className={`end-modal-history-group${isFinal ? ' end-modal-history-group-final' : ''}`}>
                        {groups.length > 1 && (
                            <span className='end-modal-history-group-label'>{isFinal ? 'Solution' : `Attempt ${groupIdx + 1}`}</span>
                        )}
                        <div className='end-modal-history-chips'>
                            {group.map((action, idx) => {
                                const { text, color } = mapActionToLabelAndColor(action);
                                return (
                                    <span key={idx} className='end-modal-history-chip' style={{ color, borderColor: color }}>
                                        {text}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const EndModal = ({ setShowEndModal, boardNum, latestBoardNum, onNavigateToBoard, isArchived, isTutorial }) => {
    const [feedback, setFeedback] = useState('');
    const historyRef = useRef(null);
    const { previous, next } = getBoardNavigation(boardNum, latestBoardNum, getPlayedBoardNumbers());
    const streak = getPlayStreak(latestBoardNum);
    const bestStreak = getBestStreak();

    useEffect(() => {
        if (historyRef.current) {
            historyRef.current.scrollTop = historyRef.current.scrollHeight;
        }
    }, [boardNum]);

    useEffect(() => {
        const handleClickOutside = (e) => e.target?.classList?.contains('end-modal') && setShowEndModal(false);
        const handleEscape = (e) => (e.key === 'Escape' || e.key === 'Esc') && setShowEndModal(false);

        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [setShowEndModal]);

    const handleShareClick = async () => {
        try {
            const result = await shareOrCopyText(buildShareText(boardNum, isArchived, streak));
            setFeedback(result === 'shared' ? 'Shared' : 'Link copied to clipboard');
        } catch {
            setFeedback('Failed to share');
        }
        setTimeout(() => setFeedback(''), 3000);
    };

    const handleBoardNavigation = async (targetBoardNum) => {
        if (!targetBoardNum || typeof onNavigateToBoard !== 'function') return;
        setShowEndModal(false);
        await onNavigateToBoard(targetBoardNum);
    };

    const score = getGameDataProperty(boardNum, 'gameScore');

    return (
        <div className='end-modal' role='dialog' aria-modal='true' tabIndex='-1'>
            <div className='end-modal-content'>
                <button className='end-modal-close' aria-label='Close' onClick={() => setShowEndModal(false)}>×</button>
                <h2 className='end-modal-title'>Well Done!</h2>
                <p className="end-modal-meta end-modal-score">Score: <strong className={getScoreClass(score)}>{score}</strong></p>
                <p className="end-modal-meta">Time: {convertMillisToSeconds(getGameDataProperty(boardNum, 'timeTaken'))}s</p>
                {streak > 0 && (
                    <p className="end-modal-meta end-modal-streak">
                        🔥 {streak} day{streak === 1 ? '' : 's'} streak
                        {bestStreak > streak ? ` (best: ${bestStreak})` : ' — new best!'}
                    </p>
                )}
                <p className="end-modal-meta" style={{ marginTop: '1rem', fontSize: '1.1rem', marginBottom: 0 }}>Actions Taken:</p>

                {getActionHistoryElements(boardNum, historyRef)}
                {isTutorial ? (
                    <div style={{ margin: '0.7rem' }}>
                        <BoardButton className='function-button play-today-button' onClick={() => setShowEndModal(false)} label={"Play Today's Board"}>Play Today's Board</BoardButton>
                    </div>
                ) : (
                    <>
                        <div style={{ margin: '0.7rem' }}>
                            <BoardButton className='function-button share-button' onClick={handleShareClick} label={'Share'}>Share</BoardButton>
                        </div>
                        {(previous !== null || next !== null) && (
                            <div className='end-modal-nav'>
                                {previous !== null && (
                                    <BoardButton className='function-button board-nav-button previous-board-button' onClick={() => handleBoardNavigation(previous)} label={`Play Board #${previous}`} />
                                )}
                                {next !== null && (
                                    <BoardButton className='function-button board-nav-button next-board-button' onClick={() => handleBoardNavigation(next)} label={`Play Board #${next}`} />
                                )}
                            </div>
                        )}
                    </>
                )}
                {feedback && <p style={{ fontSize: '1rem', margin: 0, lineHeight: 0 }}>{feedback}</p>}
            </div>
        </div >
    );
};

export default EndModal;